import { useState, useEffect, useRef, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Search,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  MapPin,
  DollarSign,
  X,
  RefreshCw,
} from "lucide-react";
import { cn, parseDateLocal, toYmdLocal } from "@/lib/utils";
import { pluralize } from "@/lib/pluralize";
import { normalizePlanType } from "@shared/schema";
import type { PlanWithCarrier } from "@shared/schema";
import { useQuoteWizardContext } from "../QuoteWizardContext";
import { getCoverageLabel } from "../constants";
import { findAreaByZip, mapWordBrownAreaLabelToId } from "@/lib/caRatingAreas";
import { rateRowCoversAge } from "@/lib/quoting/ageRateMatch";
import { planIdentity, isSamePlan } from "@/lib/quoting/planIdentity";
import { extractBenefitsSummary, withListedBenefits, withPlanBenefits } from "@/lib/quoting/benefitsSummary";
import { CoverageIcon } from "@/components/benefits/v2/typeIcons";
import { MultiSelect } from "@/components/ui/multi-select";
import { usePlanPremiumData } from "@/hooks/usePlanPremiumData";

interface Step5Props {
  fetchPlanDetailsWithRates: (plan: any) => Promise<void>;
  createPlanFromWordBrown: (plan: any, rates?: any) => Promise<number | null>;
  fetchPlanRatesForBulkImport?: (
    plan: any,
  ) => Promise<{ areaLabel: string; rates: any[]; benefits?: any[] } | null>;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatRateArea(areaLabel: string | undefined | null): string | null {
  if (!areaLabel) return null;
  const label = areaLabel.toUpperCase().trim();
  // Synthetic label for plans whose rates are not region-differentiated
  // (see server/lib/planSource/manualRateShape.ts) — say what it means.
  if (label === "STANDARD") return "ALL AREAS";
  const match = label.match(/AREA\s*(\d+)/i);
  if (match) {
    return `RA${match[1].padStart(2, "0")}`;
  }
  if (/^RA?\d+$/i.test(label)) {
    const num = label.replace(/\D/g, "");
    return `RA${num.padStart(2, "0")}`;
  }
  return label;
}

// Shared by the carrier benefits header and every plan row beneath it — one
// definition means the header labels and the row values are GUARANTEED to
// land in the same columns, instead of two separately-tweaked class strings
// drifting apart. Leading minmax(0,1fr) is a spacer under the plan name
// (its own line above) that also soaks up the row's remaining width — fixed
// px-only columns don't stretch, which left the grid stuck at its minimum
// width with the rest of the row blank. Then Ded / Office / ER / Rx / OOP /
// Premium / selection-indicator. Benefit columns flex (fr, with a floor) so
// they take the spare width instead of the spacer, and their cells wrap
// (PLAN_BENEFIT_CELL) — fixed 60-84px tracks truncated "$4,100 / $8,200",
// "$15/visit, deductible does not apply" etc. (QA note c).
const PLAN_ROW_GRID_COLS =
  "grid grid-cols-[minmax(0,0.25fr)_minmax(72px,1fr)_minmax(80px,1.4fr)_minmax(80px,1.4fr)_minmax(72px,1fr)_minmax(72px,1fr)_92px_28px] gap-x-3 items-center text-xs";
const PLAN_BENEFIT_CELL =
  "text-right min-w-0 whitespace-normal break-words leading-tight text-muted-foreground";

export function Step5PlanSelection({
  fetchPlanDetailsWithRates,
  createPlanFromWordBrown,
  fetchPlanRatesForBulkImport,
}: Step5Props) {
  const [expandedTypes, setExpandedTypes] = useState<Record<string, boolean>>(
    {},
  );
  const [expandedCarriers, setExpandedCarriers] = useState<
    Record<string, boolean>
  >({});
  // Current-plan search popover open state, per coverage type. A dedicated
  // search box, separate from the renewal carrier list — no shared "mode" to
  // enter/exit, so picking a current plan can't disturb the renewal browser.
  const [currentPlanPickerOpen, setCurrentPlanPickerOpen] = useState<
    Record<string, boolean>
  >({});
  const [selectingAll, setSelectingAll] = useState<Record<string, boolean>>({});
  const [importProgress, setImportProgress] = useState<
    Record<string, { current: number; total: number }>
  >({});
  const [clientRatingAreaLabel, setClientRatingAreaLabel] = useState<
    string | null
  >(null);
  const [addingPlanId, setAddingPlanId] = useState<string | null>(null);
  const [planRatesCache, setPlanRatesCache] = useState<
    Record<
      string,
      { rates: any[]; benefits: any[]; loading: boolean; areaLabel?: string }
    >
  >({});
  const [prefetchingTypes, setPrefetchingTypes] = useState<
    Record<string, boolean>
  >({});
  const [cachedDataLoaded, setCachedDataLoaded] = useState<boolean>(false);
  const [loadingCachedRates, setLoadingCachedRates] = useState<boolean>(false);
  const [cachedPlansFromDb, setCachedPlansFromDb] = useState<any[]>([]);
  const prevConfirmedAreaIdRef = useRef<number | null | undefined>(undefined);

  const toggleCarrier = (coverageType: string, carrier: string) => {
    const key = `${coverageType}:${carrier}`;
    setExpandedCarriers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleExpanded = (coverageType: string, plans?: any[]) => {
    const isCurrentlyExpanded = expandedTypes[coverageType];
    setExpandedTypes((prev) => ({
      ...prev,
      [coverageType]: !prev[coverageType],
    }));
    if (!isCurrentlyExpanded && plans && plans.length > 0) {
      prefetchPlansForType(coverageType, plans);
    }
  };

  const {
    quoteData,
    updateQuoteData,
    existingPlans,
    clientCurrentPlans,
    premiumSummary,
    currentPremiumSummary,
    wordBrownPlans,
    planSearchQueries,
    isSearchingPlans,
    planFilters,
    searchWordBrownPlans,
    refreshAllPlans,
    setPlanSearchQueries,
    setPlanFilters,
    setSelectedPlanForRates,
    setSelectedAreaIndex,
    setAreaSearchTerm,
    setRateDialogOpen,
    extractMetalTier,
    normalizeNetworkType,
    employees,
    dependents,
    toast,
  } = useQuoteWizardContext();

  // plan.hasRates means "has a rate somewhere, for some area" — not enough
  // to know it's priceable in THIS quote's confirmed rating area. Powers
  // the current-plan picker's area check below; the renewal browser already
  // resolves rates per-area through its own rate cache.
  const allExistingPlanIds = useMemo(() => existingPlans.map((p) => p.id), [existingPlans]);
  const areaRateClientId = quoteData.clientType === "client" ? quoteData.selectedEntity?.id : null;
  const areaRateAsOf = toYmdLocal(quoteData.effectiveDate);
  const areaRateAreaId = quoteData.confirmedRatingArea?.id ?? null;
  const { ratesByPlanId: areaRatesByPlanId, isLoading: isLoadingAreaRates } = usePlanPremiumData(
    allExistingPlanIds,
    areaRateClientId,
    areaRateAsOf,
    areaRateAreaId,
  );
  // The all-plans check above covers the whole library (thousands of ids) and
  // can take tens of seconds, during which every candidate reads as available.
  // The client's enrolled plans are the ones a broker actually picks as
  // current, so check those on their own small request and use that answer
  // first (DEV-382: an enrolled plan rated only for another area must show
  // "No rates in <area>" right away, not after the library check finishes).
  const enrolledCurrentPlanIds = useMemo(
    () =>
      clientCurrentPlans
        .filter((cp: any) => cp.isActive)
        .map((cp: any) => Number(cp.planId))
        .filter(Number.isFinite),
    [clientCurrentPlans],
  );
  const { ratesByPlanId: enrolledAreaRatesByPlanId } = usePlanPremiumData(
    enrolledCurrentPlanIds,
    areaRateClientId,
    areaRateAsOf,
    areaRateAreaId,
  );

  // Reset cache when confirmed rating area changes
  useEffect(() => {
    const currentAreaId = quoteData.confirmedRatingArea?.id || null;
    const prevAreaId = prevConfirmedAreaIdRef.current;

    // Only reset if we had a previous area and it's different
    if (prevAreaId !== undefined && prevAreaId !== currentAreaId) {
      console.log(
        `[Rating Area] Confirmed area changed from ${prevAreaId} to ${currentAreaId}, resetting cache`,
      );
      setPlanRatesCache({});
      setCachedDataLoaded(false);
      setLastCacheKey(null);
    }

    prevConfirmedAreaIdRef.current = currentAreaId;
  }, [quoteData.confirmedRatingArea?.id]);

  // Use confirmed rating area from Step 1, or fall back to ZIP-based detection
  useEffect(() => {
    // Priority 1: Use confirmed rating area from Step 1
    if (quoteData.confirmedRatingArea?.label) {
      setClientRatingAreaLabel(quoteData.confirmedRatingArea.label);
      console.log(
        `[Rating Area] Using confirmed area: ${quoteData.confirmedRatingArea.label}`,
      );
      return;
    }

    // Priority 2: Fall back to ZIP-based detection
    const fetchClientRatingArea = async () => {
      const entityZip = quoteData.selectedEntity?.zipCode;
      if (!entityZip) {
        setClientRatingAreaLabel(null);
        return;
      }
      const entityState = quoteData.selectedEntity?.state;
      const entityCounty = quoteData.selectedEntity?.county;

      try {
        const res = await fetch(
          `/api/rating-areas/lookup?zipCode=${entityZip}${entityState ? `&state=${encodeURIComponent(entityState)}` : ""}${entityCounty ? `&county=${encodeURIComponent(entityCounty)}` : ""}`,
        );
        if (res.ok) {
          const data = await res.json();
          if (data?.label) {
            setClientRatingAreaLabel(data.label);
            return;
          }
        }
      } catch (error) {
        console.error("Error fetching rating area from API:", error);
      }

      const area = findAreaByZip(entityZip);
      setClientRatingAreaLabel(area?.label || null);
    };

    fetchClientRatingArea();
  }, [quoteData.confirmedRatingArea?.label, quoteData.selectedEntity?.zipCode]);

  // Track the last loaded cache key to detect changes
  const [lastCacheKey, setLastCacheKey] = useState<string | null>(null);

  // Load cached rates from our database when rating area and effective date are known
  useEffect(() => {
    const loadCachedRates = async () => {
      if (
        !clientRatingAreaLabel ||
        !quoteData.effectiveDate ||
        loadingCachedRates
      )
        return;

      // Format effective month as YYYY-MM
      const effectiveDate = parseDateLocal(quoteData.effectiveDate as any) ?? new Date();
      const effectiveMonth = `${effectiveDate.getFullYear()}-${String(effectiveDate.getMonth() + 1).padStart(2, "0")}`;

      // Use consistent rating area ID mapping
      const ratingAreaId = mapWordBrownAreaLabelToId(clientRatingAreaLabel);
      if (!ratingAreaId) {
        console.log(
          `[Plan Cache] Could not map rating area label: ${clientRatingAreaLabel}`,
        );
        setCachedDataLoaded(true); // Mark as attempted to prevent loops
        return;
      }

      // Create a cache key to detect changes
      const cacheKey = `${ratingAreaId}:${effectiveMonth}`;

      // Skip if we already loaded for this combination
      if (lastCacheKey === cacheKey && cachedDataLoaded) return;

      // Reset cache if context changed
      if (lastCacheKey !== cacheKey) {
        setPlanRatesCache({});
        setCachedDataLoaded(false);
        setLastCacheKey(cacheKey);
      }

      setLoadingCachedRates(true);

      try {
        console.log(
          `[Plan Cache] Loading cached rates for ${ratingAreaId}, ${effectiveMonth}`,
        );

        const res = await fetch(
          `/api/plan-cache/cached-rates?ratingAreaId=${encodeURIComponent(ratingAreaId)}&effectiveMonth=${encodeURIComponent(effectiveMonth)}`,
        );

        if (res.ok) {
          const cachedRates = await res.json();

          if (cachedRates && cachedRates.length > 0) {
            console.log(
              `[Plan Cache] Loaded ${cachedRates.length} cached plan rates from database`,
            );

            // Populate the planRatesCache with cached data
            const newCache: Record<
              string,
              {
                rates: any[];
                benefits: any[];
                loading: boolean;
                areaLabel?: string;
              }
            > = {};

            // Also build fallback plan objects from cached data
            const fallbackPlans: any[] = [];

            for (const cached of cachedRates) {
              const planKey = cached.wordBrownPlanId.toString();
              newCache[planKey] = {
                rates: cached.rates || [],
                benefits: cached.benefits || [],
                loading: false,
                areaLabel:
                  clientRatingAreaLabel || cached.ratingAreaId || undefined,
              };

              // Build a plan object from cached data for fallback use
              // Parse carrierId to number for localCarrierId (createPlanFromWordBrown expects localCarrierId)
              const parsedCarrierId = cached.carrierId ? parseInt(cached.carrierId, 10) : undefined;
              fallbackPlans.push({
                id: cached.wordBrownPlanId.toString(),
                wordBrownId: cached.wordBrownPlanId.toString(),
                name: cached.planName,
                carrierName: cached.carrierName,
                carrier: cached.carrierName,
                carrierId: cached.carrierId,
                localCarrierId: !isNaN(parsedCarrierId as number) ? parsedCarrierId : undefined,
                type: cached.coverageType || "health",
                metalTier: cached.planDetails?.metalTier || cached.metalTier,
                networkType:
                  cached.planDetails?.networkType || cached.networkType,
                source: "cached",
                rates: cached.rates || [],
                benefits: cached.benefits || [],
              });
            }

            setPlanRatesCache(newCache);
            setCachedPlansFromDb(fallbackPlans);
            console.log(
              `[Plan Cache] Built ${fallbackPlans.length} fallback plans from cached data`,
            );
          } else {
            console.log(
              `[Plan Cache] No cached rates found for ${ratingAreaId}, ${effectiveMonth} - will use API prefetch`,
            );
          }
        }
      } catch (error) {
        console.error("[Plan Cache] Error loading cached rates:", error);
      } finally {
        setCachedDataLoaded(true); // Always mark as loaded to prevent loops
        setLoadingCachedRates(false);
      }
    };

    loadCachedRates();
  }, [
    clientRatingAreaLabel,
    quoteData.effectiveDate,
    cachedDataLoaded,
    loadingCachedRates,
    lastCacheKey,
  ]);

  const [prefetchProgress, setPrefetchProgress] = useState<
    Record<string, { current: number; total: number }>
  >({});

  const prefetchPlansForType = async (coverageType: string, plans: any[]) => {
    if (!fetchPlanRatesForBulkImport || prefetchingTypes[coverageType]) return;

    const wbPlans = plans.filter(
      (p) => {
        if (p.source !== "word_brown" && p.source !== "mock") return false;
        const cached = planRatesCache[p.wordBrownId || p.id];
        // Prefetch if not cached, or cached but missing both rates and benefits
        if (!cached) return true;
        if (cached.loading) return false;
        return cached.rates.length === 0 && cached.benefits.length === 0;
      },
    );
    if (wbPlans.length === 0) return;

    setPrefetchingTypes((prev) => ({ ...prev, [coverageType]: true }));
    setPrefetchProgress((prev) => ({
      ...prev,
      [coverageType]: { current: 0, total: wbPlans.length },
    }));

    const batchSize = 5;
    let processed = 0;

    // Fetch rates for ALL plans (no limit)
    for (let i = 0; i < wbPlans.length; i += batchSize) {
      const batch = wbPlans.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (plan) => {
          const planKey = plan.wordBrownId || plan.id;
          try {
            setPlanRatesCache((prev) => ({
              ...prev,
              [planKey]: { rates: [], benefits: [], loading: true },
            }));
            const result = await fetchPlanRatesForBulkImport(plan);
            if (result) {
              const benefits =
                result.benefits && result.benefits.length > 0
                  ? result.benefits
                  : plan.Benefits || plan.benefits || [];
              setPlanRatesCache((prev) => ({
                ...prev,
                [planKey]: {
                  rates: result.rates,
                  benefits,
                  loading: false,
                  areaLabel: result.areaLabel,
                },
              }));

              // Save to local cache in background (fire and forget)
              if (clientRatingAreaLabel && quoteData.effectiveDate) {
                const effectiveDate = parseDateLocal(quoteData.effectiveDate as any) ?? new Date();
                const effectiveMonth = `${effectiveDate.getFullYear()}-${String(effectiveDate.getMonth() + 1).padStart(2, "0")}`;
                const ratingAreaId = mapWordBrownAreaLabelToId(
                  clientRatingAreaLabel,
                );

                if (ratingAreaId) {
                  fetch("/api/plan-cache/cached-rates", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      wordBrownPlanId: planKey,
                      carrierId: String(
                        plan.carrierId || plan.localCarrierId || "",
                      ),
                      carrierName: plan.carrierName || "",
                      planName: plan.name || "",
                      coverageType,
                      ratingAreaId,
                      effectiveMonth,
                      rates: result.rates,
                      benefits,
                      planDetails: {
                        metalTier: plan.metalTier,
                        networkType: plan.networkType,
                      },
                    }),
                  }).catch((err) =>
                    console.log("[Plan Cache] Background save failed:", err),
                  );
                }
              }
            } else {
              setPlanRatesCache((prev) => ({
                ...prev,
                [planKey]: { rates: [], benefits: [], loading: false },
              }));
            }
          } catch (error) {
            setPlanRatesCache((prev) => ({
              ...prev,
              [planKey]: { rates: [], benefits: [], loading: false },
            }));
          }
        }),
      );

      processed += batch.length;
      setPrefetchProgress((prev) => ({
        ...prev,
        [coverageType]: { current: processed, total: wbPlans.length },
      }));
    }

    setPrefetchingTypes((prev) => ({ ...prev, [coverageType]: false }));
    setPrefetchProgress((prev) => {
      const { [coverageType]: _, ...rest } = prev;
      return rest;
    });
  };

  // Auto-prefetch benefits & rates when plans are loaded and section is expanded
  useEffect(() => {
    if (!fetchPlanRatesForBulkImport || !cachedDataLoaded) return;
    for (const coverageType of quoteData.coverageTypes) {
      const isExpanded = expandedTypes[coverageType] ?? false;
      if (!isExpanded) continue;
      const wbPlans = wordBrownPlans[coverageType] || [];
      if (wbPlans.length > 0) {
        prefetchPlansForType(coverageType, wbPlans);
      }
    }
  }, [cachedDataLoaded, expandedTypes, wordBrownPlans]);

  const calculatePlanTotalCost = useMemo(() => {
    return (plan: any): number => {
      const planKey = plan.wordBrownId || plan.id;
      const cachedData = planRatesCache[planKey];

      let rates: any[] = [];

      // Check if cached data matches the current confirmed rating area
      const cachedAreaMatches = () => {
        if (!cachedData?.areaLabel || !clientRatingAreaLabel) return true; // No area info, assume match
        const cachedAreaId = mapWordBrownAreaLabelToId(cachedData.areaLabel);
        const clientAreaId = mapWordBrownAreaLabelToId(clientRatingAreaLabel);
        // If both parse to valid IDs, compare them
        if (cachedAreaId !== undefined && clientAreaId !== undefined) return cachedAreaId === clientAreaId;
        // If cached label is a plain numeric ID, compare directly
        if (cachedAreaId === undefined && clientAreaId !== undefined) {
          const numericMatch = String(cachedData.areaLabel).trim().match(/^(\d+)$/);
          if (numericMatch) return Number(numericMatch[1]) === clientAreaId;
        }
        // Fallback: if we can't parse either, assume match (rates were loaded for this area anyway)
        return true;
      };

      if (
        cachedData?.rates &&
        cachedData.rates.length > 0 &&
        cachedAreaMatches()
      ) {
        rates = cachedData.rates;
      } else {
        const areas = plan.Areas || plan.rates || [];
        if (areas.length === 0) return 0;

        let matchingArea = areas[0];
        if (clientRatingAreaLabel) {
          const clientAreaId = mapWordBrownAreaLabelToId(clientRatingAreaLabel);
          if (clientAreaId) {
            const found = areas.find((area: any) => {
              const areaLabel =
                area.AreaName || area.areaName || area.label || "";
              const areaId = mapWordBrownAreaLabelToId(areaLabel);
              return areaId === clientAreaId;
            });
            if (found) matchingArea = found;
          }
        }

        rates = matchingArea?.Rates || matchingArea?.rates || [];
      }

      if (rates.length === 0) return 0;

      const selectedEmployeeIds = quoteData.selectedEmployees || [];
      if (selectedEmployeeIds.length === 0) return 0;

      const selectedDependentIds = new Set(quoteData.selectedDependents || []);

      let totalCost = 0;
      const effectiveDate = parseDateLocal(quoteData.effectiveDate as any) ?? new Date();

      const getAgeRate = (dob: string | Date | null | undefined): number => {
        if (!dob) return 0;
        const birthDate = parseDateLocal(dob as any) ?? new Date(0);
        const age = Math.floor(
          (effectiveDate.getTime() - birthDate.getTime()) /
            (365.25 * 24 * 60 * 60 * 1000),
        );
        const matchingRate = rates.find((r: any) => rateRowCoversAge(r, age));
        if (matchingRate) {
          return parseFloat(
            matchingRate.Premium ||
              matchingRate.premium ||
              matchingRate.Rate ||
              matchingRate.rate ||
              "0",
          );
        }
        return 0;
      };

      for (const empId of selectedEmployeeIds) {
        const employee = employees?.find((e: any) => e.id === empId);
        if (!employee?.dateOfBirth) continue;

        // Employee premium
        totalCost += getAgeRate(employee.dateOfBirth);

        // Dependent premiums
        const empDeps = dependents?.filter(
          (d: any) => d.employeeId === empId && selectedDependentIds.has(d.id),
        ) || [];
        for (const dep of empDeps) {
          totalCost += getAgeRate(dep.dateOfBirth);
        }
      }

      return totalCost;
    };
  }, [
    clientRatingAreaLabel,
    quoteData.selectedEmployees,
    quoteData.selectedDependents,
    employees,
    dependents,
    quoteData.effectiveDate,
    planRatesCache,
  ]);

  const allExpanded =
    quoteData.coverageTypes.length > 0 &&
    quoteData.coverageTypes.every((type) => expandedTypes[type]);

  const toggleAll = () => {
    const newState: Record<string, boolean> = {};
    const shouldExpand = !allExpanded;
    quoteData.coverageTypes.forEach((type) => {
      newState[type] = shouldExpand;
    });
    setExpandedTypes(newState);

    // If expanding all, trigger prefetch for each coverage type
    if (shouldExpand) {
      quoteData.coverageTypes.forEach((coverageType) => {
        const wbPlans = wordBrownPlans[coverageType] || [];
        if (wbPlans.length > 0) {
          prefetchPlansForType(coverageType, wbPlans);
        }
      });
    }
  };

  const addPlanAutomatically = async (plan: any, coverageType: string) => {
    const planUniqueId = planIdentity(plan);
    setAddingPlanId(planUniqueId);

    try {
      // Already in our DB (plan-source serves the library) — select it, never
      // re-create. createPlanFromWordBrown below is a blind POST /api/plans, so
      // sending library plans there inserted a duplicate row on every click.
      if (plan.planId) {
        const currentPlans = quoteData.selectedPlans[coverageType] || [];
        updateQuoteData({
          selectedPlans: {
            ...quoteData.selectedPlans,
            [coverageType]: [...currentPlans, { ...plan, id: plan.planId }],
          },
        });
      } else if (
        (plan.source === "word_brown" || plan.source === "mock") &&
        fetchPlanRatesForBulkImport
      ) {
        const ratesResult = await fetchPlanRatesForBulkImport(plan);
        if (!ratesResult) {
          // The carrier returned no rates for this plan/area ("unavailable").
          // Tell the broker instead of silently doing nothing.
          toast({
            title: "No rates available",
            description: `${plan.name} has no rates for this group's area, so it can't be added. Pick another plan or set rates manually.`,
            variant: "destructive",
          });
          return;
        }
        const createdPlanId = await createPlanFromWordBrown(plan, {
          areaIndex: 0,
          rates: ratesResult.rates,
          areaLabel: ratesResult.areaLabel,
          benefits: ratesResult.benefits,
        });
        if (createdPlanId) {
          const currentPlans = quoteData.selectedPlans[coverageType] || [];
          const planToAdd = { ...plan, id: createdPlanId };
          updateQuoteData({
            selectedPlans: {
              ...quoteData.selectedPlans,
              [coverageType]: [...currentPlans, planToAdd],
            },
          });
        }
      } else if (plan.source === "cached") {
        // For cached plans, we already have rates from the cache
        const cachedData = planRatesCache[plan.wordBrownId || plan.id];
        const ratesForPlan = cachedData?.rates || plan.rates || [];

        const createdPlanId = await createPlanFromWordBrown(plan, {
          areaIndex: 0,
          rates: ratesForPlan,
          areaLabel: clientRatingAreaLabel || undefined,
          benefits: cachedData?.benefits || [],
        });
        if (createdPlanId) {
          const currentPlans = quoteData.selectedPlans[coverageType] || [];
          const planToAdd = { ...plan, id: createdPlanId };
          updateQuoteData({
            selectedPlans: {
              ...quoteData.selectedPlans,
              [coverageType]: [...currentPlans, planToAdd],
            },
          });
        }
      } else {
        const currentPlans = quoteData.selectedPlans[coverageType] || [];
        updateQuoteData({
          selectedPlans: {
            ...quoteData.selectedPlans,
            [coverageType]: [...currentPlans, plan],
          },
        });
      }
    } finally {
      setAddingPlanId(null);
    }
  };

  const removePlan = (plan: any, coverageType: string) => {
    const currentPlans = quoteData.selectedPlans[coverageType] || [];
    const newPlans = currentPlans.filter(
      (p: any) => !isSamePlan(p, plan),
    );
    updateQuoteData({
      selectedPlans: {
        ...quoteData.selectedPlans,
        [coverageType]: newPlans,
      },
    });
  };

  // Current plan is one per coverage type — a new pick replaces it, unlike
  // selectedPlans above which accumulates. No Word & Brown import path here:
  // the current plan is always an existing library plan (already priced),
  // never a live carrier search result that needs creating first.
  const pickCurrentPlan = (plan: any, coverageType: string) => {
    updateQuoteData({
      currentPlans: {
        ...quoteData.currentPlans,
        [coverageType]: { ...plan, id: plan.planId || plan.id },
      },
    });
  };

  const removeCurrentPlan = (coverageType: string) => {
    updateQuoteData({
      currentPlans: {
        ...quoteData.currentPlans,
        [coverageType]: null,
      },
    });
  };

  const hasSelectedPlans = Object.values(quoteData.selectedPlans).some(
    (plans: any) => plans?.length > 0,
  );

  return (
    <div className="space-y-3 h-full flex flex-col">
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold">Select Plans</h3>
          {clientRatingAreaLabel && (
            <>
              <Badge
                variant="outline"
                className="flex items-center gap-1 text-xs"
              >
                <MapPin className="h-3 w-3" />
                {clientRatingAreaLabel} (ZIP:{" "}
                {quoteData.selectedEntity?.zipCode})
              </Badge>
              {loadingCachedRates && (
                <Badge
                  variant="secondary"
                  className="flex items-center gap-1 text-xs"
                >
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading...
                </Badge>
              )}
              {Object.keys(prefetchProgress).length > 0 && (
                <Badge
                  variant="secondary"
                  className="flex items-center gap-1 text-xs"
                >
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {Object.values(prefetchProgress).reduce(
                    (sum, p) => sum + p.current,
                    0,
                  )}
                  /
                  {Object.values(prefetchProgress).reduce(
                    (sum, p) => sum + p.total,
                    0,
                  )}
                </Badge>
              )}
              {cachedDataLoaded &&
                Object.keys(planRatesCache).length > 0 &&
                Object.keys(prefetchProgress).length === 0 && (
                  <Badge
                    variant="default"
                    className="flex items-center gap-1 text-xs bg-green-600"
                  >
                    <DollarSign className="h-3 w-3" />
                    {Object.keys(planRatesCache).length} rates
                  </Badge>
                )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={refreshAllPlans}
            disabled={Object.values(isSearchingPlans).some(Boolean)}
          >
            {Object.values(isSearchingPlans).some(Boolean) ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh Plans
              </>
            )}
          </Button>
          {quoteData.coverageTypes.length > 1 && (
            <Button variant="outline" size="sm" onClick={toggleAll}>
              {allExpanded ? (
                <>
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Collapse All
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Expand All
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-6 flex-1 min-h-0">
        <div className="flex-1 space-y-6 min-w-0 overflow-y-auto pb-4">
          {quoteData.coverageTypes.map((coverageType) => {
            const selectedCarrierIds =
              quoteData.selectedCarriers[coverageType] || [];
            const wbPlans = wordBrownPlans[coverageType] || [];
            const currentClientId =
              quoteData.clientType === "client"
                ? quoteData.selectedEntity?.id
                : null;

            const dbPlans = existingPlans.filter(
              (plan: PlanWithCarrier) =>
                normalizePlanType(plan.type) ===
                  normalizePlanType(coverageType) &&
                !!plan.carrierId &&
                selectedCarrierIds.includes(plan.carrierId) &&
                (!plan.clientId || plan.clientId === currentClientId),
            );

            // Keyed on planIdentity (our row id). The old `wordBrownId ||
            // name-carrier-metalTier` key was null for every library plan, so the
            // same plan showed twice when one copy had a metal tier and one didn't.
            const seenPlans = new Map<string, any>();
            wbPlans.forEach((plan) => {
              const uniqueKey =
                planIdentity(plan) ??
                `${plan.name}-${plan.carrierName}-${plan.metalTier}`;
              if (!seenPlans.has(uniqueKey)) {
                seenPlans.set(uniqueKey, {
                  ...plan,
                  source: plan.source || "word_brown",
                });
              }
            });

            // If Word & Brown API failed or returned no plans, use cached fallback plans
            if (wbPlans.length === 0 && cachedPlansFromDb.length > 0) {
              const filteredCachedPlans = cachedPlansFromDb.filter(
                (plan) =>
                  normalizePlanType(plan.type) ===
                  normalizePlanType(coverageType),
              );
              filteredCachedPlans.forEach((plan) => {
                const uniqueKey =
                  plan.wordBrownId ||
                  `${plan.name}-${plan.carrierName}-${plan.metalTier}`;
                if (!seenPlans.has(uniqueKey)) {
                  seenPlans.set(uniqueKey, { ...plan, source: "cached" });
                }
              });
              if (filteredCachedPlans.length > 0) {
                console.log(
                  `[Plan Cache] Using ${filteredCachedPlans.length} cached plans as fallback for ${coverageType}`,
                );
              }
            }

            const allPlans = Array.from(seenPlans.values());
            const isLoading = isSearchingPlans[coverageType];
            const searchQuery = planSearchQueries[coverageType] || "";
            const currentFilters = planFilters[coverageType] || {
              carriers: [],
              metalTiers: [],
              networkTypes: [],
              planTypes: [],
            };

            const uniqueCarriers = Array.from(
              new Set(
                allPlans.map((p) => p.carrierName || p.carrier).filter(Boolean),
              ),
            ).sort() as string[];

            const uniqueMetalTiers = Array.from(
              new Set(
                allPlans
                  .map((p) => extractMetalTier(p))
                  .filter((t): t is string => !!t),
              ),
            ).sort();

            const uniqueNetworkTypes = Array.from(
              new Set(
                allPlans
                  .map((p) => normalizeNetworkType(p.networkType))
                  .filter((n): n is string => !!n),
              ),
            ).sort();

            const extractPlanType = (plan: any): string | null => {
              const name = plan.name || "";
              if (name.toLowerCase().includes("hsa") || plan.hsaEligible)
                return "HSA-Qualified";
              if (name.toLowerCase().includes("hra")) return "HRA";
              if (
                name.toLowerCase().includes("hdhp") ||
                name.toLowerCase().includes("high deductible")
              )
                return "HDHP";
              return null;
            };

            const uniquePlanTypes = Array.from(
              new Set(allPlans.map((p) => extractPlanType(p)).filter(Boolean)),
            ).sort() as string[];

            const planHasClientRatingArea = (plan: any): boolean => {
              if (plan.source !== "word_brown") return true;
              if (!clientRatingAreaLabel) return true;
              const planRates = plan.rates || plan.Areas || [];
              if (!Array.isArray(planRates) || planRates.length === 0)
                return true;
              const clientAreaId = mapWordBrownAreaLabelToId(
                clientRatingAreaLabel,
              );
              if (!clientAreaId) return true;
              return planRates.some((rate: any) => {
                const areaLabel =
                  rate.AreaName ||
                  rate.areaName ||
                  rate.Name ||
                  rate.name ||
                  "";
                const areaId = mapWordBrownAreaLabelToId(areaLabel);
                return areaId === clientAreaId;
              });
            };

            const availablePlans = allPlans.filter((plan) => {
              const planCarrier = plan.carrierName || plan.carrier;
              const planMetalTier = extractMetalTier(plan);
              const planNetworkType = normalizeNetworkType(plan.networkType);
              const planType = extractPlanType(plan);

              const matchesCarrier =
                currentFilters.carriers.length === 0 ||
                currentFilters.carriers.includes(planCarrier);
              const matchesMetalTier =
                currentFilters.metalTiers.length === 0 ||
                (planMetalTier &&
                  currentFilters.metalTiers.includes(planMetalTier));
              const matchesNetworkType =
                currentFilters.networkTypes.length === 0 ||
                (planNetworkType &&
                  currentFilters.networkTypes.includes(planNetworkType));
              const matchesPlanType =
                currentFilters.planTypes.length === 0 ||
                (planType && currentFilters.planTypes.includes(planType));
              const matchesRatingArea = planHasClientRatingArea(plan);

              return (
                matchesCarrier &&
                matchesMetalTier &&
                matchesNetworkType &&
                matchesPlanType &&
                matchesRatingArea
              );
            });

            const plansByCarrier = availablePlans.reduce(
              (acc: Record<string, any[]>, plan) => {
                const carrier =
                  plan.carrierName || plan.carrier || "Unknown Carrier";
                if (!acc[carrier]) acc[carrier] = [];
                acc[carrier].push(plan);
                return acc;
              },
              {},
            );

            const sortedCarriers = Object.keys(plansByCarrier).sort();

            const isExpanded = expandedTypes[coverageType] ?? false;
            const selectedPlansCount =
              quoteData.selectedPlans[coverageType]?.length || 0;
            const isCompareOn = !!quoteData.compareCurrentEnabled[coverageType];
            const currentPlan = quoteData.currentPlans[coverageType] ?? null;
            const isCurrentPickerOpen = currentPlanPickerOpen[coverageType] ?? false;
            // Candidates for the current-plan search: existing library plans
            // of this coverage type. The current plan is always an already-
            // known plan (never a live carrier search result to create), so
            // this is a plain filter, not a fetch. Split into what the
            // client is actually enrolled in today (pinned first) vs. the
            // rest of the library — empty for a prospect, who was never
            // enrolled anywhere, so this collapses back to one plain group.
            const enrolledPlanIds = new Set(
              clientCurrentPlans
                .filter(
                  (cp: any) =>
                    cp.isActive &&
                    normalizePlanType(cp.plan?.type) === normalizePlanType(coverageType),
                )
                .map((cp: any) => cp.planId),
            );
            // Sourced from existingPlans, not clientCurrentPlans' own nested
            // `plan` (which /api/client-plans never enriches with hasRates)
            // — one source of truth so the no-rates check below actually
            // applies to a client's already-enrolled plans too.
            const typeCandidates = existingPlans.filter(
              (plan: PlanWithCarrier) =>
                normalizePlanType(plan.type) === normalizePlanType(coverageType),
            );
            const enrolledPlanCandidates = typeCandidates.filter((plan: PlanWithCarrier) =>
              enrolledPlanIds.has(plan.id),
            );
            const otherPlanCandidates = typeCandidates.filter(
              (plan: PlanWithCarrier) => !enrolledPlanIds.has(plan.id),
            );
            // The carrier may already have a filed renewal for the picked
            // current plan in the library (plans.renewedFromPlanId) — surface
            // it as a one-click suggestion instead of making the broker
            // search for it separately. Never auto-adds on its own.
            const suggestedRenewalPlan = currentPlan
              ? existingPlans.find(
                  (plan: PlanWithCarrier) =>
                    (plan as any).renewedFromPlanId === currentPlan.id &&
                    normalizePlanType(plan.type) === normalizePlanType(coverageType),
                )
              : null;
            const suggestedRenewalAlreadyAdded = suggestedRenewalPlan
              ? quoteData.selectedPlans[coverageType]?.some((p: any) =>
                  isSamePlan(p, suggestedRenewalPlan),
                )
              : false;
            const renderCurrentPlanOption = (plan: any) => {
              // Without rates there's nothing to compare the premium against
              // — same "No rates" treatment as the renewal list below, so a
              // plan that can't be quoted can't be picked as current either.
              // Assume available while the area check is still loading, to
              // avoid flashing every candidate as disabled.
              const noRatesAnywhere = plan.hasRates === false;
              const enrolledAreaRates = enrolledAreaRatesByPlanId[plan.id];
              const noRatesInArea =
                !noRatesAnywhere &&
                (enrolledAreaRates !== undefined
                  ? enrolledAreaRates.length === 0
                  : !isLoadingAreaRates && (areaRatesByPlanId[plan.id]?.length ?? 0) === 0);
              const noRates = noRatesAnywhere || noRatesInArea;
              return (
                <CommandItem
                  key={plan.id}
                  value={`${plan.name} ${plan.carrierName || plan.carrier || ""}`}
                  disabled={noRates}
                  onSelect={() => {
                    if (noRates) return;
                    pickCurrentPlan(plan, coverageType);
                    setCurrentPlanPickerOpen((prev) => ({
                      ...prev,
                      [coverageType]: false,
                    }));
                  }}
                >
                  <div className="flex flex-1 min-w-0 flex-col">
                    <span className="flex items-center gap-2 text-sm">
                      <span className="truncate">{plan.name}</span>
                      {noRates && (
                        <Badge
                          variant="outline"
                          className="text-[10px] shrink-0 border-amber-500 text-amber-700 dark:text-amber-500"
                        >
                          {noRatesAnywhere
                            ? "No rates"
                            : `No rates in ${clientRatingAreaLabel || "this area"}`}
                        </Badge>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {plan.carrierName || plan.carrier}
                    </span>
                  </div>
                </CommandItem>
              );
            };
            // Total selected for this coverage type, regardless of the
            // current search/filter - a broker can "Select All" under one
            // search, change the search, and "Select All" again, building up
            // a selection that spans several different filtered views. Once
            // any of that exists, reuse this same button as a global clear
            // instead of requiring them to revisit every past search to
            // toggle each batch off one at a time.
            const selectedCount =
              quoteData.selectedPlans[coverageType]?.length || 0;

            const handleSelectAll = async () => {
              if (selectedCount > 0) {
                updateQuoteData({
                  selectedPlans: {
                    ...quoteData.selectedPlans,
                    [coverageType]: [],
                  },
                });
              } else {
                setSelectingAll((prev) => ({ ...prev, [coverageType]: true }));
                try {
                  const currentPlans =
                    quoteData.selectedPlans[coverageType] || [];
                  const plansToAdd = availablePlans.filter(
                    (plan) => !currentPlans.some((p: any) => isSamePlan(p, plan)),
                  );
                  setImportProgress((prev) => ({
                    ...prev,
                    [coverageType]: { current: 0, total: plansToAdd.length },
                  }));

                  const addedPlans: any[] = [];
                  for (let i = 0; i < plansToAdd.length; i++) {
                    const plan = plansToAdd[i];
                    setImportProgress((prev) => ({
                      ...prev,
                      [coverageType]: {
                        current: i + 1,
                        total: plansToAdd.length,
                      },
                    }));
                    try {
                      const hasLocalRates =
                        plan.rates?.length > 0 || plan.Areas?.length > 0;
                      const isExternalPlan =
                        plan.source === "word_brown" || plan.source === "mock";
                      const isCachedPlan = plan.source === "cached";

                      if (isExternalPlan && fetchPlanRatesForBulkImport) {
                        const ratesResult =
                          await fetchPlanRatesForBulkImport(plan);
                        if (ratesResult) {
                          const createdPlanId = await createPlanFromWordBrown(
                            plan,
                            {
                              areaIndex: 0,
                              rates: ratesResult.rates,
                              areaLabel: ratesResult.areaLabel,
                              benefits: ratesResult.benefits,
                            },
                          );
                          if (createdPlanId) {
                            addedPlans.push({ ...plan, id: createdPlanId });
                          }
                        }
                      } else if (isCachedPlan) {
                        // For cached plans, we already have rates from the cache
                        const cachedData =
                          planRatesCache[plan.wordBrownId || plan.id];
                        const ratesForPlan =
                          cachedData?.rates || plan.rates || [];

                        const createdPlanId = await createPlanFromWordBrown(
                          plan,
                          {
                            areaIndex: 0,
                            rates: ratesForPlan,
                            areaLabel: clientRatingAreaLabel || undefined,
                            benefits: cachedData?.benefits || [],
                          },
                        );
                        if (createdPlanId) {
                          addedPlans.push({ ...plan, id: createdPlanId });
                        }
                      } else {
                        addedPlans.push(plan);
                      }
                    } catch (error) {
                      console.error(`Failed to add plan ${plan.name}:`, error);
                    }
                  }

                  updateQuoteData({
                    selectedPlans: {
                      ...quoteData.selectedPlans,
                      [coverageType]: [...currentPlans, ...addedPlans],
                    },
                  });
                } finally {
                  setSelectingAll((prev) => ({
                    ...prev,
                    [coverageType]: false,
                  }));
                  setImportProgress((prev) => {
                    const newProgress = { ...prev };
                    delete newProgress[coverageType];
                    return newProgress;
                  });
                }
              }
            };

            return (
              <Card key={coverageType}>
                <Collapsible
                  open={isExpanded}
                  onOpenChange={() => toggleExpanded(coverageType, allPlans)}
                >
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <CoverageIcon planType={coverageType} size="sm" />
                          <CardTitle className="text-base">
                            {getCoverageLabel(coverageType)}
                          </CardTitle>
                          <Badge variant="outline" className="ml-2">
                            {selectedPlansCount} selected
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          {prefetchingTypes[coverageType] && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              <span>Loading rates...</span>
                            </div>
                          )}
                          <Badge variant="secondary">
                            {isLoading ? (
                              <span className="flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Loading plans...
                              </span>
                            ) : (
                              `${availablePlans.length} ${pluralize(availablePlans.length, "plan")} available`
                            )}
                          </Badge>
                          <label
                            className="flex items-center gap-2 pl-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-sm font-medium">
                              Compare vs. current
                            </span>
                            <Switch
                              checked={isCompareOn}
                              onCheckedChange={(checked) =>
                                updateQuoteData({
                                  compareCurrentEnabled: {
                                    ...quoteData.compareCurrentEnabled,
                                    [coverageType]: checked,
                                  },
                                })
                              }
                            />
                          </label>
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardHeader className="pt-0 pb-4 space-y-4">
                      {isCompareOn && (
                        <div className="space-y-2">
                          {/* Dedicated search, independent of the renewal
                              carrier list below — no shared "mode", so picking
                              a current plan can never disturb that list. */}
                          <Popover
                            open={isCurrentPickerOpen}
                            onOpenChange={(open) =>
                              setCurrentPlanPickerOpen((prev) => ({
                                ...prev,
                                [coverageType]: open,
                              }))
                            }
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-between font-normal"
                              >
                                <span className="flex items-center gap-2 text-muted-foreground">
                                  <Search className="h-3.5 w-3.5" />
                                  {currentPlan ? "Change current plan" : "Set current plan"}
                                </span>
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-[--radix-popover-trigger-width] p-0"
                              align="start"
                            >
                              <Command>
                                <CommandInput placeholder="Search plans..." />
                                <CommandEmpty>No plans found.</CommandEmpty>
                                <CommandList>
                                  {enrolledPlanCandidates.length > 0 && (
                                    <CommandGroup heading="Currently enrolled">
                                      {enrolledPlanCandidates.map(renderCurrentPlanOption)}
                                    </CommandGroup>
                                  )}
                                  <CommandGroup
                                    heading={
                                      enrolledPlanCandidates.length > 0
                                        ? "All plans"
                                        : undefined
                                    }
                                  >
                                    {otherPlanCandidates.map(renderCurrentPlanOption)}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>

                          {currentPlan && (
                            <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-900 dark:bg-amber-950/30">
                              <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200 dark:bg-amber-900 dark:text-amber-200">
                                CURRENT
                              </Badge>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">
                                  {currentPlan.carrierName || currentPlan.carrier
                                    ? `${currentPlan.carrierName || currentPlan.carrier} — `
                                    : ""}
                                  {currentPlan.name}
                                </div>
                                <div className="text-xs text-amber-800 dark:text-amber-300">
                                  What this group has today
                                </div>
                              </div>
                              <button
                                onClick={() => removeCurrentPlan(coverageType)}
                                className="p-1 hover:bg-destructive/10 hover:text-destructive rounded transition-colors shrink-0"
                                title="Remove current plan"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}

                          {suggestedRenewalPlan && !suggestedRenewalAlreadyAdded && (
                            <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900 dark:bg-blue-950/30">
                              <div className="flex-1 min-w-0 text-sm">
                                This plan has a renewal option:{" "}
                                <span className="font-medium">
                                  {suggestedRenewalPlan.name}
                                </span>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={() =>
                                  addPlanAutomatically(suggestedRenewalPlan, coverageType)
                                }
                              >
                                Add to renewal options
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search plans..."
                            className="pl-9"
                            value={searchQuery}
                            onChange={(e) =>
                              setPlanSearchQueries((prev) => ({
                                ...prev,
                                [coverageType]: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchWordBrownPlans(coverageType, searchQuery);
                              }
                            }}
                          />
                        </div>
                        <Button
                          variant="outline"
                          onClick={() =>
                            searchWordBrownPlans(coverageType, searchQuery)
                          }
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Searching...
                            </>
                          ) : (
                            <>
                              <Search className="h-4 w-4 mr-2" />
                              Search
                            </>
                          )}
                        </Button>
                        {availablePlans.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSelectAll}
                            disabled={selectingAll[coverageType]}
                            className="whitespace-nowrap"
                          >
                            {selectingAll[coverageType] ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                {importProgress[coverageType]
                                  ? `${importProgress[coverageType].current}/${importProgress[coverageType].total}`
                                  : "Importing..."}
                              </>
                            ) : selectedCount > 0 ? (
                              <>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Deselect All ({selectedCount})
                              </>
                            ) : (
                              "Select All"
                            )}
                          </Button>
                        )}
                      </div>

                      {allPlans.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground mb-1.5 block">
                              Carriers
                            </Label>
                            <MultiSelect
                              options={uniqueCarriers.map((c) => ({
                                label: c,
                                value: c,
                              }))}
                              selected={currentFilters.carriers}
                              onChange={(values) => {
                                setPlanFilters((prev) => ({
                                  ...prev,
                                  [coverageType]: {
                                    ...currentFilters,
                                    carriers: values,
                                  },
                                }));
                              }}
                              placeholder="All Carriers"
                              searchPlaceholder="Search carriers..."
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground mb-1.5 block">
                              Metal Tier
                            </Label>
                            <MultiSelect
                              options={uniqueMetalTiers.map((t) => ({
                                label: t,
                                value: t,
                              }))}
                              selected={currentFilters.metalTiers}
                              onChange={(values) => {
                                setPlanFilters((prev) => ({
                                  ...prev,
                                  [coverageType]: {
                                    ...currentFilters,
                                    metalTiers: values,
                                  },
                                }));
                              }}
                              placeholder="All Tiers"
                              searchPlaceholder="Search tiers..."
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground mb-1.5 block">
                              Network Type
                            </Label>
                            <MultiSelect
                              options={uniqueNetworkTypes.map((n) => ({
                                label: n,
                                value: n,
                              }))}
                              selected={currentFilters.networkTypes}
                              onChange={(values) => {
                                setPlanFilters((prev) => ({
                                  ...prev,
                                  [coverageType]: {
                                    ...currentFilters,
                                    networkTypes: values,
                                  },
                                }));
                              }}
                              placeholder="All Networks"
                              searchPlaceholder="Search networks..."
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground mb-1.5 block">
                              Plan Type
                            </Label>
                            <MultiSelect
                              options={uniquePlanTypes.map((t) => ({
                                label: t,
                                value: t,
                              }))}
                              selected={currentFilters.planTypes}
                              onChange={(values) => {
                                setPlanFilters((prev) => ({
                                  ...prev,
                                  [coverageType]: {
                                    ...currentFilters,
                                    planTypes: values,
                                  },
                                }));
                              }}
                              placeholder="All Types"
                              searchPlaceholder="Search types..."
                              disabled={uniquePlanTypes.length === 0}
                            />
                          </div>
                        </div>
                      )}
                    </CardHeader>

                    <CardContent>
                      {isLoading ? (
                        <div className="text-sm text-muted-foreground text-center py-8">
                          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
                          Searching plans...
                        </div>
                      ) : availablePlans.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-4">
                          {selectedCarrierIds.length === 0
                            ? "Please select carriers first"
                            : allPlans.length > 0
                              ? "No plans match the selected filters. Try adjusting your filter criteria."
                              : "No plans found. Try searching or adjusting your criteria."}
                        </div>
                      ) : (
                        <div className="space-y-6">
                          {allPlans.length > availablePlans.length && (
                            <div className="text-sm text-muted-foreground">
                              Showing {availablePlans.length} of{" "}
                              {allPlans.length} {pluralize(allPlans.length, "plan")}
                            </div>
                          )}

                          {sortedCarriers.map((carrier) => {
                            const metalTierOrder: Record<string, number> = {
                              Platinum: 1,
                              Gold: 2,
                              Silver: 3,
                              Bronze: 4,
                              "Minimum Coverage": 5,
                              Catastrophic: 6,
                            };
                            const getMetalTierSortOrder = (
                              plan: any,
                            ): number => {
                              const tier = extractMetalTier(plan);
                              return tier ? metalTierOrder[tier] || 99 : 99;
                            };
                            const carrierPlans = [
                              ...plansByCarrier[carrier],
                            ].sort((a, b) => {
                              const tierA = getMetalTierSortOrder(a);
                              const tierB = getMetalTierSortOrder(b);
                              if (tierA !== tierB) return tierA - tierB;
                              return (a.name || "").localeCompare(b.name || "");
                            });
                            const carrierKey = `${coverageType}:${carrier}`;
                            const isCarrierExpanded =
                              expandedCarriers[carrierKey] ?? false;
                            const selectedInCarrier = carrierPlans.filter(
                              (p: any) =>
                                quoteData.selectedPlans[coverageType]?.some(
                                  (sp: any) => isSamePlan(sp, p),
                                ),
                            ).length;

                            return (
                              <div
                                key={carrier}
                                className="border border-primary/30 rounded-lg overflow-hidden"
                              >
                                <div
                                  className="flex items-center justify-between px-3 py-2 bg-primary/10 cursor-pointer hover:bg-primary/20 transition-colors"
                                  onClick={() =>
                                    toggleCarrier(coverageType, carrier)
                                  }
                                >
                                  <div className="flex items-center gap-2">
                                    {isCarrierExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-primary" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-primary" />
                                    )}
                                    <span className="font-medium text-sm text-primary">
                                      {carrier}
                                    </span>
                                    <Badge
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      {carrierPlans.length}
                                    </Badge>
                                    {selectedInCarrier > 0 && (
                                      <Badge
                                        variant="default"
                                        className="text-xs bg-primary"
                                      >
                                        {selectedInCarrier} selected
                                      </Badge>
                                    )}
                                  </div>
                                </div>

                                {isCarrierExpanded && (
                                  <>
                                    {/* Header once per carrier, not repeated as
                                        "Label:" text on every row — same
                                        PLAN_ROW_GRID_COLS as every row below,
                                        so labels and values share columns
                                        exactly, all the way out to Premium. */}
                                    <div className="px-3 pb-1.5 pt-2">
                                      <div
                                        className={cn(
                                          PLAN_ROW_GRID_COLS,
                                          "font-semibold uppercase tracking-wide text-muted-foreground text-[10px]",
                                        )}
                                      >
                                        <span />
                                        <span className="text-right">Ded</span>
                                        <span className="text-right">Office</span>
                                        <span className="text-right">ER</span>
                                        <span className="text-right">Rx</span>
                                        <span className="text-right">OOP</span>
                                        <span className="text-right">Premium</span>
                                        <span />
                                      </div>
                                    </div>
                                  <div className="divide-y">
                                    {carrierPlans.map((plan: any) => {
                                      // Rates-cache key stays `wordBrownId || id`
                                      // — that's what the prefetch paths write.
                                      // Selection uses planIdentity (planId first).
                                      const planKey =
                                        plan.wordBrownId || plan.id;
                                      const isSelected = quoteData.selectedPlans[
                                        coverageType
                                      ]?.some((p: any) => isSamePlan(p, plan));
                                      const isCurrentPick = isCompareOn && currentPlan
                                        ? isSamePlan(currentPlan, plan)
                                        : false;
                                      const isAddingThisPlan =
                                        addingPlanId === planIdentity(plan);
                                      const cachedData =
                                        planRatesCache[planKey];
                                      const isLoadingRates =
                                        cachedData?.loading;

                                      const planWithCachedData =
                                        withPlanBenefits(plan, cachedData?.benefits);
                                      const benefitsSummary =
                                        extractBenefitsSummary(
                                          planWithCachedData,
                                        );
                                      const estimatedTotal =
                                        calculatePlanTotalCost(plan);
                                      // Two ways a plan can't be priced: the
                                      // server says it has no usable rates, or
                                      // the rate lookup finished for this
                                      // plan/area and came back empty (the
                                      // "No rate" label below). Either way it
                                      // must not be selectable — picking one
                                      // used to fail much later, in the premium
                                      // calculator. An absent hasRates (a source
                                      // that doesn't report it) never disables.
                                      const rateLookupEmpty =
                                        !!cachedData &&
                                        !cachedData.loading &&
                                        estimatedTotal <= 0;
                                      const noRates =
                                        plan.hasRates === false || rateLookupEmpty;

                                      return (
                                        <div
                                          key={planKey}
                                          data-testid={`quote-plan-row-${planIdentity(plan)}`}
                                          className={cn(
                                            "px-3 py-2 transition-all",
                                            isCurrentPick
                                              ? "opacity-50 cursor-default"
                                              : noRates
                                                ? "opacity-60 cursor-not-allowed"
                                                : isAddingThisPlan
                                                  ? "cursor-wait opacity-70"
                                                  : "cursor-pointer hover:bg-muted/30",
                                            isSelected && "bg-primary/10",
                                          )}
                                          title={
                                            isCurrentPick
                                              ? "Already set as the current plan above."
                                              : rateLookupEmpty
                                                ? "No rate for this group's area and effective date, so it can't be quoted."
                                                : noRates
                                                  ? "This plan has no rates set up, so it can't be quoted."
                                                  : undefined
                                          }
                                          onClick={async () => {
                                            if (isCurrentPick || noRates || addingPlanId) return;
                                            if (isSelected) {
                                              removePlan(plan, coverageType);
                                            } else {
                                              await addPlanAutomatically(
                                                plan,
                                                coverageType,
                                              );
                                            }
                                          }}
                                        >
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-medium truncate">
                                              {plan.name}
                                            </span>
                                            {noRates && (
                                              <Badge
                                                variant="outline"
                                                className="text-xs shrink-0 border-amber-500 text-amber-700 dark:text-amber-500"
                                              >
                                                No rates
                                              </Badge>
                                            )}
                                            {plan.metalTier && (
                                              <Badge
                                                variant="outline"
                                                className="text-xs shrink-0"
                                              >
                                                {plan.metalTier}
                                              </Badge>
                                            )}
                                            {plan.networkType && (
                                              <Badge
                                                variant="secondary"
                                                className="text-xs shrink-0"
                                              >
                                                {plan.networkType}
                                              </Badge>
                                            )}
                                            {cachedData?.areaLabel &&
                                              formatRateArea(
                                                cachedData.areaLabel,
                                              ) && (
                                                <Badge
                                                  variant="outline"
                                                  className="text-xs shrink-0 bg-blue-50 text-blue-700 border-blue-200"
                                                >
                                                  {formatRateArea(
                                                    cachedData.areaLabel,
                                                  )}
                                                </Badge>
                                              )}
                                          </div>
                                          {/* One grid for the whole row — same
                                              template as the carrier header
                                              above, Premium/indicator included,
                                              so nothing is a separately-floated
                                              flex child drifting from the
                                              benefit columns. */}
                                          <div className={PLAN_ROW_GRID_COLS}>
                                            <span />
                                            <span
                                              className={PLAN_BENEFIT_CELL}
                                              title={benefitsSummary.deductible || undefined}
                                              data-testid="plan-benefit-ded"
                                            >
                                              {benefitsSummary.deductible || "—"}
                                            </span>
                                            <span
                                              className={PLAN_BENEFIT_CELL}
                                              title={benefitsSummary.officeVisit || undefined}
                                              data-testid="plan-benefit-office"
                                            >
                                              {benefitsSummary.officeVisit || "—"}
                                            </span>
                                            <span
                                              className={PLAN_BENEFIT_CELL}
                                              title={benefitsSummary.er || undefined}
                                              data-testid="plan-benefit-er"
                                            >
                                              {benefitsSummary.er || "—"}
                                            </span>
                                            <span
                                              className={PLAN_BENEFIT_CELL}
                                              title={
                                                benefitsSummary.rxTier1 ||
                                                benefitsSummary.rxTier2 ||
                                                undefined
                                              }
                                            >
                                              {benefitsSummary.rxTier1 ||
                                                benefitsSummary.rxTier2 ||
                                                "—"}
                                            </span>
                                            <span
                                              className={PLAN_BENEFIT_CELL}
                                              title={benefitsSummary.oopMax || undefined}
                                              data-testid="plan-benefit-oop"
                                            >
                                              {benefitsSummary.oopMax || "—"}
                                            </span>
                                            <span className="text-right truncate">
                                              {isLoadingRates ? (
                                                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground ml-auto" />
                                              ) : estimatedTotal > 0 ? (
                                                <span className="text-sm font-semibold text-primary whitespace-nowrap">
                                                  {formatCurrency(estimatedTotal)}/mo
                                                </span>
                                              ) : cachedData ? (
                                                // Rate lookup finished but the
                                                // carrier had no rate cached for
                                                // this plan/month. Show it so the
                                                // row isn't blank.
                                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                  No rate
                                                </span>
                                              ) : null}
                                            </span>
                                            <span className="flex justify-end">
                                              {isCurrentPick ? (
                                                // The 28px indicator column only
                                                // fits an icon, not "CURRENT" text
                                                // (that overflowed into Premium) —
                                                // the amber row above already
                                                // says it in full.
                                                <span
                                                  className="h-2.5 w-2.5 rounded-full bg-amber-500"
                                                  title="This is the current plan"
                                                />
                                              ) : isAddingThisPlan ? (
                                                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                              ) : isSelected ? (
                                                <CheckCircle className="h-4 w-4 text-primary" />
                                              ) : (
                                                <div className="h-4 w-4 border rounded-full" />
                                              )}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {isCompareOn && currentPlan && selectedPlansCount > 0 && (
                        <div className="mt-6 border rounded-lg overflow-hidden">
                          <div className="px-3 py-2 bg-muted/50 border-b text-sm font-semibold">
                            Current vs. renewal
                          </div>
                          {(() => {
                            const renewalPlans =
                              quoteData.selectedPlans[coverageType] || [];
                            const currentBreakdown =
                              currentPremiumSummary.planBreakdowns?.find(
                                (b: any) => b.plan?.id === currentPlan.id,
                              );
                            const currentPremium =
                              currentBreakdown?.totalMonthlyPremium || 0;
                            const currentBenefits =
                              extractBenefitsSummary(withListedBenefits(currentPlan, wordBrownPlans[coverageType]));

                            const columns = renewalPlans.map((plan: any) => {
                              const breakdown =
                                premiumSummary.planBreakdowns?.find(
                                  (b: any) => b.plan?.id === plan.id,
                                );
                              const premium =
                                breakdown?.totalMonthlyPremium || 0;
                              const delta = premium - currentPremium;
                              const deltaPct =
                                currentPremium > 0
                                  ? (delta / currentPremium) * 100
                                  : 0;
                              return {
                                plan,
                                premium,
                                delta,
                                deltaPct,
                                benefits: extractBenefitsSummary(withListedBenefits(plan, wordBrownPlans[coverageType])),
                              };
                            });

                            return (
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b">
                                      <th className="text-left font-medium text-muted-foreground px-3 py-2 w-40" />
                                      <th className="text-center font-semibold text-amber-900 bg-amber-50 px-3 py-2 dark:bg-amber-950/30 dark:text-amber-200">
                                        Current
                                      </th>
                                      {columns.map(({ plan }) => (
                                        <th
                                          key={plan.id}
                                          className="text-center font-semibold text-primary px-3 py-2"
                                        >
                                          {plan.name}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr className="border-t">
                                      <td className="px-3 py-2 font-medium">
                                        Monthly Premium
                                      </td>
                                      <td className="text-center font-semibold px-3 py-2 bg-amber-50/60 dark:bg-amber-950/20">
                                        {formatCurrency(currentPremium)}
                                      </td>
                                      {columns.map(
                                        ({ plan, premium, delta, deltaPct }) => (
                                          <td
                                            key={plan.id}
                                            className="text-center px-3 py-2"
                                          >
                                            <div>{formatCurrency(premium)}</div>
                                            {currentPremium > 0 &&
                                              delta !== 0 && (
                                                <div
                                                  className={cn(
                                                    "text-[11px] font-semibold",
                                                    delta > 0
                                                      ? "text-red-600"
                                                      : "text-green-600",
                                                  )}
                                                >
                                                  {delta > 0 ? "▲" : "▼"}{" "}
                                                  {formatCurrency(
                                                    Math.abs(delta),
                                                  )}{" "}
                                                  ({delta > 0 ? "+" : ""}
                                                  {deltaPct.toFixed(0)}%)
                                                </div>
                                              )}
                                          </td>
                                        ),
                                      )}
                                    </tr>
                                    <tr className="border-t">
                                      <td className="px-3 py-2 font-medium">
                                        Medical Deductible
                                      </td>
                                      <td className="text-center px-3 py-2 bg-amber-50/60 dark:bg-amber-950/20">
                                        {currentBenefits.deductible || "—"}
                                      </td>
                                      {columns.map(({ plan, benefits }) => (
                                        <td
                                          key={plan.id}
                                          className="text-center px-3 py-2"
                                        >
                                          {benefits.deductible || "—"}
                                        </td>
                                      ))}
                                    </tr>
                                    <tr className="border-t">
                                      <td className="px-3 py-2 font-medium">
                                        Out-of-Pocket Max
                                      </td>
                                      <td className="text-center px-3 py-2 bg-amber-50/60 dark:bg-amber-950/20">
                                        {currentBenefits.oopMax || "—"}
                                      </td>
                                      {columns.map(({ plan, benefits }) => (
                                        <td
                                          key={plan.id}
                                          className="text-center px-3 py-2"
                                        >
                                          {benefits.oopMax || "—"}
                                        </td>
                                      ))}
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>

        <div className="hidden lg:flex lg:flex-col w-80 shrink-0 max-h-full">
          <Card className="flex flex-col flex-1 min-h-0">
            <CardHeader className="pb-2 shrink-0">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span>Selected Plans</span>
                {hasSelectedPlans && (
                  <Badge variant="secondary" className="text-xs">
                    {Object.values(quoteData.selectedPlans).reduce(
                      (sum: number, plans: any) => sum + (plans?.length || 0),
                      0,
                    )}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 flex-1 overflow-y-auto min-h-0">
              {hasSelectedPlans ? (
                <div className="space-y-4">
                  {Object.entries(quoteData.selectedPlans).map(
                    ([coverageType, plans]: [string, any]) => {
                      if (!plans?.length) return null;

                      // Group plans by carrier
                      const plansByCarrier: Record<string, any[]> = {};
                      plans.forEach((plan: any) => {
                        const carrierName =
                          plan.carrierName || plan.carrier || "Other";
                        if (!plansByCarrier[carrierName]) {
                          plansByCarrier[carrierName] = [];
                        }
                        plansByCarrier[carrierName].push(plan);
                      });

                      return (
                        <div key={coverageType} className="space-y-2">
                          {/* Coverage type is the bigger grouping — size,
                              weight, icon and a full underline make it read
                              as a real section break. Carrier (below) is
                              deliberately quieter and indented so it reads
                              as nested under it, not another heading. */}
                          <div className="flex items-center gap-1.5 pb-1.5 border-b-2 border-primary/30">
                            <CoverageIcon planType={coverageType} size="sm" />
                            <span className="text-sm font-bold">
                              {getCoverageLabel(coverageType)}
                            </span>
                          </div>
                          {Object.entries(plansByCarrier).map(
                            ([carrierName, carrierPlans]) => (
                              <div key={carrierName} className="space-y-1">
                                <div className="flex items-center gap-1 pl-1 text-[11px] font-normal text-muted-foreground">
                                  <span className="text-[9px]">&#9671;</span>
                                  {carrierName}
                                </div>
                                {carrierPlans.map((plan: any) => {
                                  const planKey = plan.wordBrownId || plan.id;
                                  const cachedData = planRatesCache[planKey];
                                  const planWithCachedData = withPlanBenefits(
                                    plan,
                                    cachedData?.benefits,
                                    wordBrownPlans[coverageType],
                                  );
                                  const planBenefits =
                                    extractBenefitsSummary(planWithCachedData);
                                  const hasBenefits =
                                    Object.keys(planBenefits).length > 0;
                                  const breakdown =
                                    premiumSummary?.planBreakdowns?.find(
                                      (b: any) =>
                                        b.plan?.id === plan.id ||
                                        b.plan?.name === plan.name,
                                    );

                                  return (
                                    <div
                                      key={planKey}
                                      className="ml-2.5 flex items-start justify-between gap-2 p-2 bg-muted/50 border rounded-md text-xs group"
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">
                                          {plan.name}
                                        </div>
                                        {hasBenefits && (
                                          <div className="text-muted-foreground mt-0.5">
                                            {planBenefits.deductible && (
                                              <span>
                                                Ded: {planBenefits.deductible}
                                              </span>
                                            )}
                                            {planBenefits.oopMax && (
                                              <span className="ml-2">
                                                OOP: {planBenefits.oopMax}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                        {breakdown &&
                                          breakdown.totalMonthlyPremium > 0 && (
                                            <div className="text-primary font-medium mt-0.5">
                                              {formatCurrency(
                                                breakdown.totalMonthlyPremium,
                                              )}
                                              /mo
                                            </div>
                                          )}
                                      </div>
                                      <button
                                        onClick={() =>
                                          removePlan(plan, coverageType)
                                        }
                                        className="p-1 hover:bg-destructive/10 hover:text-destructive rounded transition-colors opacity-0 group-hover:opacity-100"
                                        title="Remove plan"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            ),
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-4">
                  Click on plans to select them
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
